import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  TableContainer,
  Paper,
  FormControlLabel,
  Switch,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { PersonAdd, Delete, PermIdentity, Home } from "@material-ui/icons";
import React, { useEffect, useState } from "react";
import { OrganisersType } from "../../types/game";
import { UserBrifObj } from "../../types/user";
import SearchUser from "../Search/SearchUser";
import { SearchItem } from "../Search/SearchInput";

export interface RuleSharingColumnConfig {
  /** Set when editing an existing custom rule: owner is chosen only with these row switches. */
  showOwnerColumn: boolean;
  ownerId: string;
  onOwnerIdChange: (userId: string) => void;
  canEditOwner: boolean;
  /** Light border; keeps this section distinct from the rest of the form. */
  tableFrame?: boolean;
}

interface Props {
  organisers?: UserBrifObj[];
  insideHandler?: boolean;
  viewOnly?: boolean;
  onAdd?(user: UserBrifObj): void;
  onDelete?(user: UserBrifObj): void;
  onChange?(organisers: UserBrifObj[]): void;
  /** Custom rule sharing: owner via switches, same table as ad-hoc organiser lists. */
  ruleSharing?: RuleSharingColumnConfig;
}

const gameAddUserGreen = "#399e39";

const useAddUserControlStyles = makeStyles(() => ({
  addUserBlock: {
    display: "inline-block",
    "& .MuiListItemText-primary": {
      color: gameAddUserGreen,
      fontWeight: 600,
    },
    "& .MuiListItem-button:hover": {
      backgroundColor: "rgba(57, 158, 57, 0.1)",
    },
  },
}));

function organiserFullName(organiser: UserBrifObj) {
  const s = [organiser.name, organiser.lastName].filter(Boolean).join(" ").trim();
  return s || organiser.username || "";
}

export const GameAdditionalOrganisersTable = (props: Props) => {
  const [organisers, setOrganisers] = useState<UserBrifObj[]>(props.organisers || ([] as UserBrifObj[]));
  const addUserClasses = useAddUserControlStyles();
  const rule = props.ruleSharing;
  const showOwner = !!rule?.showOwnerColumn;
  const tableFrame = rule?.tableFrame !== false && showOwner;
  const colCount = props.viewOnly ? 3 : 4;

  const addOrganiser = (user: UserBrifObj) => {
    const _organisers = [...organisers];
    if (_organisers.filter(o => o._id === user._id).length > 0) return;
    _organisers.push(user);
    setOrganisers(_organisers);
    props.onChange && props.onChange(_organisers);
  };

  const removeOrganiser = (user: UserBrifObj) => {
    const _organisers = organisers.filter(organiser => organiser._id !== user._id);
    setOrganisers(_organisers);
    props.onChange && props.onChange(_organisers);
  };

  useEffect(() => setOrganisers(props.organisers || ([] as UserBrifObj[])), [props.organisers]);

  const showNameHeaders = organisers.length > 0 || showOwner;
  const ownerIdStr = rule?.ownerId != null ? String(rule.ownerId) : "";

  const handleOwnerSwitch = (rowId: string, next: boolean) => {
    if (!rule) return;
    if (next) {
      rule.onOwnerIdChange(rowId);
    }
  };

  const canRemoveRow = (organiser: UserBrifObj) => {
    if (organiser.type === OrganisersType.Owner) return false;
    if (showOwner && ownerIdStr && String(organiser._id) === ownerIdStr) return false;
    return true;
  };

  const removeReason = (organiser: UserBrifObj) => {
    if (organiser.type === OrganisersType.Owner && !showOwner) return "";
    if (showOwner && ownerIdStr && String(organiser._id) === ownerIdStr) {
      return "Choose another owner with the switch before removing this person.";
    }
    return "Remove organiser";
  };

  return (
    <TableContainer
      component={Paper}
      elevation={tableFrame ? 0 : 1}
      style={
        tableFrame
          ? {
              border: "1px solid rgba(0, 0, 0, 0.08)",
              borderRadius: 8,
              overflow: "hidden",
            }
          : undefined
      }
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            {showNameHeaders && (
              <>
                {showOwner ? (
                  <TableCell align="center" style={{ fontWeight: 600, width: 96 }}>
                    Owner
                  </TableCell>
                ) : (
                  <TableCell style={{ width: 48 }} />
                )}
                <TableCell>Full name</TableCell>
                <TableCell>Username</TableCell>
              </>
            )}
            {!props.viewOnly && (
              <TableCell align="right" style={{ width: 72 }}>
                <div className={addUserClasses.addUserBlock}>
                  <SearchUser
                    style={{ width: 58 }}
                    dialog
                    autoClearText
                    title=" "
                    icon={<PersonAdd style={{ color: gameAddUserGreen }} />}
                    selectedUsers={organisers.map(
                      o =>
                        ({
                          value: o._id,
                          title: organiserFullName(o),
                          subtitle: o.username,
                          object: o,
                        } as SearchItem),
                    )}
                    onUserSelected={(user: any) => {
                      if (user && props.insideHandler) addOrganiser(user.object);
                      if (user && props.onAdd) props.onAdd(user.object);
                    }}
                    minChar={3}
                  />
                </div>
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {organisers.length === 0 && showOwner && (
            <TableRow>
              <TableCell colSpan={colCount} style={{ padding: "20px 16px" }}>
                <Typography variant="body2" color="textSecondary">
                  Add organisers with the plus control. The owner (one person) is selected with the switch in each row.
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {organisers.map(organiser => {
            const showDeleteControl = organiser.type !== OrganisersType.Owner || showOwner;
            return (
            <TableRow key={String(organiser._id)}>
              {showOwner ? (
                <TableCell align="center" style={{ verticalAlign: "middle" }}>
                  <FormControlLabel
                    style={{ margin: 0 }}
                    control={
                      <Switch
                        size="small"
                        color="primary"
                        checked={ownerIdStr !== "" && String(organiser._id) === ownerIdStr}
                        onChange={(_e, checked) => handleOwnerSwitch(String(organiser._id), checked)}
                        disabled={!rule?.canEditOwner}
                        inputProps={{ "aria-label": "Owner" }}
                      />
                    }
                    label=""
                  />
                </TableCell>
              ) : (
                <TableCell>
                  {organiser.type === OrganisersType.Owner ? (
                    <Tooltip title="Game owner">
                      <PermIdentity />
                    </Tooltip>
                  ) : (
                    organiser.type === OrganisersType.ClubManager && (
                      <Tooltip title="Club manager">
                        <Home />
                      </Tooltip>
                    )
                  )}
                </TableCell>
              )}
              <TableCell>{organiserFullName(organiser)}</TableCell>
              <TableCell>{organiser.username}</TableCell>
              {!props.viewOnly && (
                <TableCell align="right">
                  {!showDeleteControl ? null : canRemoveRow(organiser) ? (
                    <Tooltip title="Remove organiser">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (props.insideHandler) removeOrganiser(organiser);
                            if (props.onDelete) props.onDelete(organiser);
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : (
                    <Tooltip title={removeReason(organiser)}>
                      <span>
                        <IconButton size="small" disabled>
                          <Delete fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
              )}
            </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
